import { Injectable, inject, signal } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable, of, throwError } from 'rxjs'
import { catchError, finalize, map, take, tap } from 'rxjs/operators'
import { environment } from 'src/environments/environment'
import {
    dashboardEndpoints,
    userHierarchyConstants,
    valueConstants,
} from '@app/core/constants/valueConstants'
import { DEPT_CONSTANTS } from '@app/core/constants/generate-proposal.constants'
import { UserStore } from '@core/services/user/user.store'
import { IUserHierarchyApiResponse, IUserHierarchyRecord } from '@core/models/user-hierarchy.model'

const REPORTING_HIERARCHY_QUERY = {
    USER_IDS: 'user_ids',
    PAGE: 'page',
    LIMIT: 'limit',
} as const

@Injectable({ providedIn: 'root' })
export class UserHierarchyService {
    private readonly http = inject(HttpClient)
    private readonly userStore = inject(UserStore)
    private readonly apiBaseUrl = environment.apiBaseUrl

    private readonly hierarchyCache = new Map<string, IUserHierarchyRecord>()

    readonly hierarchy = signal<IUserHierarchyRecord | null>(null)
    readonly isLoading = signal<boolean>(false)
    readonly error = signal<string | null>(null)

    getReportingHierarchy(
        userIds: string[],
        page = 1,
        limit = 25,
    ): Observable<IUserHierarchyApiResponse> {
        const params = new HttpParams()
            .set(REPORTING_HIERARCHY_QUERY.USER_IDS, userIds.join(','))
            .set(REPORTING_HIERARCHY_QUERY.PAGE, page.toString())
            .set(REPORTING_HIERARCHY_QUERY.LIMIT, limit.toString())

        const url = `${this.apiBaseUrl}${dashboardEndpoints.REPORTING_HIERARCHY_PATH}`

        return this.http
            .get<IUserHierarchyApiResponse>(url, { params, withCredentials: true })
            .pipe(
                catchError(() =>
                    of({
                        success: false,
                        message: '',
                        data: [],
                    }),
                ),
            )
    }

    getHierarchy(userId?: string): Observable<IUserHierarchyRecord> {
        const resolvedUserId = (userId || this.userStore.getUserData()?.user_id || '').trim()
        if (!resolvedUserId) {
            const errorMessage = 'User ID is required to fetch hierarchy'
            this.error.set(errorMessage)
            return throwError(() => new Error(errorMessage))
        }

        const cachedHierarchy = this.hierarchyCache.get(resolvedUserId)
        if (cachedHierarchy) {
            this.hierarchy.set(cachedHierarchy)
            this.error.set(null)
            return of(cachedHierarchy)
        }

        this.isLoading.set(true)
        this.error.set(null)

        return this.fetchHierarchyRecord(resolvedUserId).pipe(
            tap((record) => {
                this.hierarchyCache.set(resolvedUserId, record)
                this.hierarchy.set(record)
                this.error.set(null)
            }),
            catchError((error: unknown) => {
                const message =
                    error instanceof Error ? error.message : 'Failed to fetch user hierarchy'
                this.error.set(message)
                return throwError(() => new Error(message))
            }),
            finalize(() => this.isLoading.set(false)),
        )
    }

    getDeptId(userId?: string): Observable<number> {
        return this.getHierarchy(userId).pipe(
            map((record) => {
                const deptId = this.normalizeDeptId(record.dept_id)
                if (deptId === null) {
                    throw new Error('Invalid department ID in user hierarchy')
                }
                return deptId
            }),
        )
    }

    getCachedDeptId(userId?: string): number | null {
        const resolvedUserId = (userId || this.userStore.getUserData()?.user_id || '').trim()
        if (!resolvedUserId) {
            return null
        }

        const cachedHierarchy = this.hierarchyCache.get(resolvedUserId)
        if (!cachedHierarchy) {
            return null
        }

        return this.normalizeDeptId(cachedHierarchy.dept_id)
    }

    preloadHierarchy(userId?: string): void {
        this.getHierarchy(userId)
            .pipe(take(1))
            .subscribe({
                next: () => {},
                error: () => {},
            })
    }

    isAcadDept(userId?: string): Observable<boolean> {
        return this.getDeptId(userId).pipe(map((deptId) => deptId === DEPT_CONSTANTS.ACAD))
    }

    isSalesDept(userId?: string): Observable<boolean> {
        return this.getDeptId(userId).pipe(map((deptId) => deptId === DEPT_CONSTANTS.SALES))
    }

    clearCache(): void {
        this.hierarchyCache.clear()
        this.hierarchy.set(null)
        this.error.set(null)
        this.isLoading.set(false)
    }

    private fetchHierarchyRecord(userId: string): Observable<IUserHierarchyRecord> {
        const url = `${this.apiBaseUrl}${dashboardEndpoints.REPORTING_HIERARCHY_PATH}`
        const params = new HttpParams()
            .set(REPORTING_HIERARCHY_QUERY.USER_IDS, userId)
            .set(REPORTING_HIERARCHY_QUERY.PAGE, String(valueConstants.DEFAULT_PAGE))
            .set(REPORTING_HIERARCHY_QUERY.LIMIT, String(userHierarchyConstants.PAGE_LIMIT))

        return this.http
            .get<IUserHierarchyApiResponse>(url, { params, withCredentials: true })
            .pipe(
                map((response) => {
                    if (!response.success) {
                        throw new Error(response.message || 'Failed to fetch user hierarchy')
                    }

                    const record = this.findUserInResponse(response, userId)
                    if (!record) {
                        throw new Error('User hierarchy data not found')
                    }

                    return record
                }),
            )
    }

    private normalizeDeptId(deptId: number | string | null | undefined): number | null {
        const parsed = Number(deptId)
        return Number.isFinite(parsed) ? parsed : null
    }

    private findUserInResponse(
        response: IUserHierarchyApiResponse,
        userId: string,
    ): IUserHierarchyRecord | null {
        if (!response.data) {
            return null
        }

        if (Array.isArray(response.data)) {
            return (
                response.data.find((hierarchyRecord) => hierarchyRecord.user_id === userId) ?? null
            )
        }

        return response.data.user_id === userId ? response.data : null
    }
}
